import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api";
import { identify, resetIdentity } from "./analytics";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identify(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    // Hydrate the full user from /auth/me (includes company_billing) so redirect
    // logic and the dashboard see complete data immediately, not the minimal
    // login response. Falls back to the minimal user if /auth/me fails.
    const full = await checkAuth();
    return full || data.user;
  };

  // Google Identity Services: exchange the Google ID token for our own JWT (same as email/password).
  const loginWithGoogle = async (credential, role = "candidate") => {
    const { data } = await api.post("/auth/google", { credential, role });
    localStorage.setItem("token", data.token);
    const full = await checkAuth();
    return full || data.user;
  };

  const signup = async (email, password, name, role, targetJobTitle) => {
    const { data } = await api.post("/auth/signup", {
      email, password, name, role,
      target_job_title: targetJobTitle || null,
    });
    localStorage.setItem("token", data.token);
    const full = await checkAuth();
    return full || data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("original_token");
    setUser(null);
    resetIdentity();
  };

  const impersonate = async (targetRole) => {
    const { data } = await api.post("/auth/impersonate", { target_role: targetRole });
    // Save current token so we can restore it on exit
    const current = localStorage.getItem("token");
    if (current) localStorage.setItem("original_token", current);
    localStorage.setItem("token", data.token);
    // Refresh user from /me so state reflects impersonated role
    await checkAuth();
  };

  const exitImpersonation = async () => {
    const original = localStorage.getItem("original_token");
    if (original) {
      localStorage.setItem("token", original);
      localStorage.removeItem("original_token");
    }
    await checkAuth();
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, loginWithGoogle, signup, logout, checkAuth, impersonate, exitImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user) {
      // Force password change before allowing access to any protected page
      if (user.requires_password_change && window.location.pathname !== "/change-password") {
        navigate("/change-password");
        return;
      }
      // Superusers can visit any route without redirect
      if (user.is_superuser) return;
      if (user.role === "admin" && window.location.pathname === "/dashboard") navigate("/admin");
      // Only redirect active companies from /dashboard → /company/roles.
      // Pending/locked companies stay on /dashboard where the activation page is shown.
      const isActiveCompany = (user.role === "company" || user.role === "company_employee")
        && user?.company_billing?.company_status === "active";
      if (isActiveCompany && window.location.pathname === "/dashboard") navigate("/company/roles");
      // Treat company_employee as equivalent to company for role-gated routes
      const effectiveRole = user.role === "company_employee" ? "company" : user.role;
      if (role && effectiveRole !== role) {
        if (user.role === "admin") navigate("/admin");
        // Send company users to /dashboard — ActiveCompanyRoute handles the rest
        else if (user.role === "company" || user.role === "company_employee") navigate("/dashboard");
        else navigate("/dashboard");
      }
    }
  }, [user, loading, role, navigate]);
  if (loading || !user) return <div className="p-12 font-mono text-sm text-muted-foreground">Checking session…</div>;
  return children;
}
